/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { cn } from '@/shared/ui/utils/tailwind';
import { useTranslation } from 'react-i18next';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';

interface LogoRingSpinnerProps {
  text?: string;
  leaving?: boolean;
  className?: string;
  /** Overall diameter in px. Defaults to 140. */
  size?: number;
}

const DEFAULT_SIZE = 140;
const BASE_R_OUTER = 54;
const BASE_R_INNER = 42;
const BASE_LOGO_HEIGHT = 64;

const LogoRingSpinner: React.FC<LogoRingSpinnerProps> = ({
  text,
  leaving = false,
  className,
  size = DEFAULT_SIZE,
}) => {
  const { t } = useTranslation('common');
  const scale = size / DEFAULT_SIZE;
  const cx = size / 2;
  const rOuter = BASE_R_OUTER * scale;
  const rInner = BASE_R_INNER * scale;
  const circOuter = 2 * Math.PI * rOuter;
  const circInner = 2 * Math.PI * rInner;
  const logoHeight = Math.round(BASE_LOGO_HEIGHT * scale);

  return (
    <>
      {/*
        transform-origin lives on each circle's own inline style, not here --
        .rl4-cw/.rl4-ccw are shared across every instance on the page, and a
        size-dependent origin baked into the shared rule meant the
        last-mounted instance's origin won for ALL instances. Two spinners of
        different sizes on screen at once each spun around the wrong center.
      */}
      <style>{`
        @keyframes rl4-spin-cw  { to { transform: rotate(360deg); } }
        @keyframes rl4-spin-ccw { to { transform: rotate(-360deg); } }
        .rl4-cw  { animation: rl4-spin-cw  1.6s linear infinite; }
        .rl4-ccw { animation: rl4-spin-ccw 1.1s linear infinite; }
        @keyframes rl4-msg-in { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
        .rl4-msg-in { animation: rl4-msg-in 0.3s ease both; }
        @keyframes rl4-out { to { opacity:0; } }
        .rl4-out { animation: rl4-out 0.45s ease both; }
      `}</style>

      <div
        data-testid="logo-ring-spinner"
        className={cn(
          'relative flex flex-col items-center justify-center',
          leaving && 'rl4-out',
          className,
        )}
        role="status"
        aria-label={t('a11y.loading')}
      >
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="absolute inset-0"
            style={{ overflow: 'visible' }}
          >
            {/* Outer ring track */}
            <circle
              cx={cx} cy={cx} r={rOuter}
              fill="none"
              stroke="var(--seed-base)"
              strokeWidth={Math.max(1, 1 * scale)}
              opacity="0.08"
            />
            {/* Outer ring — CW, tapered */}
            <circle
              className="rl4-cw"
              cx={cx} cy={cx} r={rOuter}
              fill="none"
              stroke="var(--seed-base)"
              strokeWidth={Math.max(1.5, 2.5 * scale)}
              strokeLinecap="round"
              strokeDasharray={`${circOuter * 0.72} ${circOuter * 0.28}`}
              opacity="0.9"
              style={{ transformOrigin: `${cx}px ${cx}px` }}
            />
            {/* Inner ring — CCW, shorter dash */}
            <circle
              className="rl4-ccw"
              cx={cx} cy={cx} r={rInner}
              fill="none"
              stroke="var(--seed-base)"
              strokeWidth={Math.max(1, 1.5 * scale)}
              strokeLinecap="round"
              strokeDasharray={`${circInner * 0.35} ${circInner * 0.65}`}
              style={{ transformOrigin: `${cx}px ${cx}px` }}
              opacity="0.4"
            />
          </svg>

          <HousienBrandLogo variant="icon" height={logoHeight} />
        </div>

        <span
          data-testid="ring-spinner-text"
          className={cn('text-sm text-(--text-secondary)', text ? 'rl4-msg-in' : '')}
          style={{
            position: 'absolute',
            top: '100%',
            marginTop: '16px',
            opacity: text ? 1 : 0,
            visibility: text ? 'visible' : 'hidden',
          }}
        >
          {text || '\u00A0'}
        </span>
      </div>
    </>
  );
};

export default LogoRingSpinner;
